import React from "react";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import { Campaign, Network } from "../../types";
import CampaignTypeEditor from './CampaignTypeEditor';
import { useCampaign } from '../../contexts/useCampaign';

interface CampaignDetailsProps {
  network: Network;
  campaign: Campaign;
}

const CampaignDetails: React.FC<CampaignDetailsProps> = ({
  network,
  campaign
}) => {
  const { setSelectedCampaign } = useCampaign();
  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : "-";

  return (
    <div className="p-3">
      {/* Campaign */}
      <Card bg="light" className="mb-3">
        <Card.Header>Campaign Details</Card.Header>
        <Card.Body>
          <CampaignTypeEditor key={`${campaign.id}-${campaign.type}`} campaign={campaign} onSaved={setSelectedCampaign} />
          <Row>
            <Col md={6}>
              <p><strong>Campaign ID:</strong> <code>{campaign.id}</code></p>
              <p><strong>Name:</strong> {campaign.name}</p>
              <p><strong>Status:</strong> {campaign.status}</p>
              <p><strong>Bitrix ID:</strong> {campaign.bitrix_id ?? "-"}</p>
            </Col>
            <Col md={6}>
              <p><strong>Start Date:</strong> {formatDate(campaign.start_date)}</p>
              <p><strong>End Date:</strong> {formatDate(campaign.end_date)}</p>
              <p><strong>Created:</strong> {formatDate(campaign.created_at)}</p>
              <p><strong>Updated:</strong> {formatDate(campaign.updated_at)}</p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Network */}
      <Row>
        <Col md={6}>
          <Card bg="light" className="mb-3">
            <Card.Header>Network - Basic Information</Card.Header>
            <Card.Body>
              <p><strong>Full Name:</strong> {network.nameLong}</p>
              <p><strong>Country:</strong> {network.country}</p>
              <p><strong>Role:</strong> {network.role}</p>
              <p><strong>Length:</strong> {network.length}</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card bg="light" className="mb-3">
            <Card.Header>Network - Technical Details</Card.Header>
            <Card.Body>
              <p><strong>UUID:</strong> <code>{network.uuid}</code></p>
              <p><strong>Slug:</strong> <code>{network.slug}</code></p>
              <p><strong>Created:</strong> {formatDate(network.createdAt)}</p>
              <p><strong>Updated:</strong> {formatDate(network.updatedAt)}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {network.boundingBox && Object.keys(network.boundingBox).length > 0 && (
        <Card bg="light" className="mb-3">
          <Card.Header>Bounding Box</Card.Header>
          <Card.Body>
            <pre className="mb-0"><code>{JSON.stringify(network.boundingBox, null, 2)}</code></pre>
          </Card.Body>
        </Card>
      )}

      {network.modules && network.modules.length > 0 && (
        <Card bg="light" className="mb-3">
          <Card.Header>Modules</Card.Header>
          <Card.Body>
            <pre className="mb-0"><code>{JSON.stringify(network.modules, null, 2)}</code></pre>
          </Card.Body>
        </Card>
      )}

      {/* Photo Collections */}
      <Card bg="light" className="mb-3">
        <Card.Header>Photo Collections</Card.Header>
        <Card.Body>
          {(
            <div className="table-responsive">
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Layer</th>
                    <th>UUID</th>
                  </tr>
                </thead>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default CampaignDetails;
